export const DEFAULT_LAYER_FILTERS = {
    blur: 0,
    saturation: 1,
    brightness: 1,
    contrast: 1,
    blendMode: 'normal',
}

export const LAYER_BLEND_MODES = [
    { value: 'normal', label: 'Normal' },
    { value: 'multiply', label: 'Multiply' },
    { value: 'screen', label: 'Screen' },
    { value: 'overlay', label: 'Overlay' },
    { value: 'darken', label: 'Darken' },
    { value: 'lighten', label: 'Lighten' },
    { value: 'color-dodge', label: 'Color dodge' },
    { value: 'color-burn', label: 'Color burn' },
    { value: 'hard-light', label: 'Hard light' },
    { value: 'soft-light', label: 'Soft light' },
    { value: 'difference', label: 'Difference' },
    { value: 'exclusion', label: 'Exclusion' },
]

export function normalizeLayerFilters(layer) {
    const f = layer?.filters || {}
    return {
        blur: Math.max(0, f.blur ?? DEFAULT_LAYER_FILTERS.blur),
        saturation: Math.max(0, f.saturation ?? DEFAULT_LAYER_FILTERS.saturation),
        brightness: Math.max(0, f.brightness ?? DEFAULT_LAYER_FILTERS.brightness),
        contrast: Math.max(0, f.contrast ?? DEFAULT_LAYER_FILTERS.contrast),
        blendMode: f.blendMode || DEFAULT_LAYER_FILTERS.blendMode,
    }
}

export function getLayerFilterPipeline(filters) {
    const f = normalizeLayerFilters({ filters })
    const ops = []
    if (Math.abs(f.saturation - 1) > 1e-6) ops.push({ type: 'saturate', value: f.saturation })
    if (Math.abs(f.contrast - 1) > 1e-6) ops.push({ type: 'contrast', value: f.contrast })
    if (Math.abs(f.brightness - 1) > 1e-6) ops.push({ type: 'brightness', value: f.brightness })
    if (f.blur > 1e-6) ops.push({ type: 'blur', value: f.blur })
    const pad = Math.max(2, Math.ceil(f.blur * 2.5) + 2)
    return { ops, pad, f }
}

export function layerNeedsSvgFilter(filters) {
    return getLayerFilterPipeline(filters).ops.length > 0
}

export function blendModeToCss(mode) {
    const m = mode || 'normal'
    if (m === 'normal') return undefined
    return m
}

export function blendModeToCanvas(mode) {
    const m = mode || 'normal'
    if (m === 'normal') return 'source-over'
    return m
}

/** Append fe* primitives to a <filter> element (DOM). */
export function appendSvgFilterPrimitives(svgNs, filterEl, w, h, filters) {
    const { ops, pad } = getLayerFilterPipeline(filters)
    if (!ops.length) return false
    filterEl.setAttribute('filterUnits', 'userSpaceOnUse')
    filterEl.setAttribute('x', String(-pad))
    filterEl.setAttribute('y', String(-pad))
    filterEl.setAttribute('width', String(w + pad * 2))
    filterEl.setAttribute('height', String(h + pad * 2))
    let inName = 'SourceGraphic'
    ops.forEach((op, i) => {
        const isLast = i === ops.length - 1
        const outId = isLast ? null : `fx${i}`
        if (op.type === 'saturate') {
            const fe = document.createElementNS(svgNs, 'feColorMatrix')
            fe.setAttribute('in', inName)
            fe.setAttribute('type', 'saturate')
            fe.setAttribute('values', String(op.value))
            if (outId) fe.setAttribute('result', outId)
            filterEl.appendChild(fe)
        } else if (op.type === 'contrast') {
            const fe = document.createElementNS(svgNs, 'feComponentTransfer')
            fe.setAttribute('in', inName)
            if (outId) fe.setAttribute('result', outId)
            const intercept = 0.5 * (1 - op.value)
            for (const ch of ['R', 'G', 'B']) {
                const fn = document.createElementNS(svgNs, `feFunc${ch}`)
                fn.setAttribute('type', 'linear')
                fn.setAttribute('slope', String(op.value))
                fn.setAttribute('intercept', String(intercept))
                fe.appendChild(fn)
            }
            const fnA = document.createElementNS(svgNs, 'feFuncA')
            fnA.setAttribute('type', 'identity')
            fe.appendChild(fnA)
            filterEl.appendChild(fe)
        } else if (op.type === 'brightness') {
            const fe = document.createElementNS(svgNs, 'feComponentTransfer')
            fe.setAttribute('in', inName)
            if (outId) fe.setAttribute('result', outId)
            for (const ch of ['R', 'G', 'B']) {
                const fn = document.createElementNS(svgNs, `feFunc${ch}`)
                fn.setAttribute('type', 'linear')
                fn.setAttribute('slope', String(op.value))
                fn.setAttribute('intercept', '0')
                fe.appendChild(fn)
            }
            const fnA = document.createElementNS(svgNs, 'feFuncA')
            fnA.setAttribute('type', 'identity')
            fe.appendChild(fnA)
            filterEl.appendChild(fe)
        } else if (op.type === 'blur') {
            const fe = document.createElementNS(svgNs, 'feGaussianBlur')
            fe.setAttribute('in', inName)
            fe.setAttribute('stdDeviation', String(op.value))
            if (outId) fe.setAttribute('result', outId)
            filterEl.appendChild(fe)
        }
        if (outId) inName = outId
    })
    return true
}

export function SvgLayerFilterDefs({ filterId, canvasWidth, canvasHeight, filters }) {
    const { ops, pad } = getLayerFilterPipeline(filters || {})
    if (!ops.length) return null
    let inName = 'SourceGraphic'
    const children = []
    ops.forEach((op, i) => {
        const isLast = i === ops.length - 1
        const outId = isLast ? undefined : `${filterId}-fx${i}`
        if (op.type === 'saturate') {
            children.push(
                <feColorMatrix
                    key={`${filterId}-sat-${i}`}
                    in={inName}
                    type="saturate"
                    values={String(op.value)}
                    {...(outId ? { result: outId } : {})}
                />
            )
        } else if (op.type === 'contrast') {
            const intercept = 0.5 * (1 - op.value)
            children.push(
                <feComponentTransfer key={`${filterId}-con-${i}`} in={inName} {...(outId ? { result: outId } : {})}>
                    <feFuncR type="linear" slope={op.value} intercept={intercept} />
                    <feFuncG type="linear" slope={op.value} intercept={intercept} />
                    <feFuncB type="linear" slope={op.value} intercept={intercept} />
                    <feFuncA type="identity" />
                </feComponentTransfer>
            )
        } else if (op.type === 'brightness') {
            children.push(
                <feComponentTransfer key={`${filterId}-br-${i}`} in={inName} {...(outId ? { result: outId } : {})}>
                    <feFuncR type="linear" slope={op.value} intercept={0} />
                    <feFuncG type="linear" slope={op.value} intercept={0} />
                    <feFuncB type="linear" slope={op.value} intercept={0} />
                    <feFuncA type="identity" />
                </feComponentTransfer>
            )
        } else if (op.type === 'blur') {
            children.push(
                <feGaussianBlur
                    key={`${filterId}-bl-${i}`}
                    in={inName}
                    stdDeviation={op.value}
                    {...(outId ? { result: outId } : {})}
                />
            )
        }
        if (outId) inName = outId
    })
    return (
        <defs>
            <filter
                id={filterId}
                filterUnits="userSpaceOnUse"
                x={-pad}
                y={-pad}
                width={canvasWidth + pad * 2}
                height={canvasHeight + pad * 2}
            >
                {children}
            </filter>
        </defs>
    )
}
